import { useEffect, useState } from "react";
import { api, extractError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useBrands } from "@/contexts/BrandContext";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Trash2, RefreshCw, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

export default function DataManagement() {
  const { user } = useAuth();
  const { brands, selectedBrandId } = useBrands();
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scope, setScope] = useState("current"); // "current" | "all"

  const [confirmTarget, setConfirmTarget] = useState(null); // upload doc
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  const isAdmin = user?.role === "admin";

  const load = () => {
    if (!isAdmin) return;
    setLoading(true);
    const url = scope === "current" && selectedBrandId
      ? `/admin/uploads?brand_id=${selectedBrandId}`
      : "/admin/uploads";
    api.get(url)
      .then((r) => setUploads(r.data || []))
      .catch((e) => toast.error(extractError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [isAdmin, scope, selectedBrandId]);

  const onDelete = async () => {
    if (!confirmTarget) return;
    if (confirmText !== confirmTarget.id) {
      toast.error("Confirmation text must equal the upload id");
      return;
    }
    setDeleting(true);
    try {
      const r = await api.delete(`/admin/uploads/${confirmTarget.id}?confirm=${confirmTarget.id}`);
      const d = r.data?.deleted || {};
      const total = Object.values(d).reduce((a, b) => a + (b || 0), 0);
      toast.success(`Upload deleted — ${total} dependent records removed`);
      setConfirmTarget(null); setConfirmText("");
      load();
    } catch (e) {
      toast.error(extractError(e));
    } finally { setDeleting(false); }
  };

  if (!isAdmin) {
    return (
      <div className="rounded-xl border border-rose-200 bg-rose-50 p-6 text-rose-900">
        <div className="font-semibold text-lg flex items-center gap-2"><ShieldAlert className="h-5 w-5" /> Admins only</div>
        <p className="mt-1 text-sm">Data Management is restricted to administrators.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="data-management-page">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400">Admin</p>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Data Management</h1>
          <p className="mt-1 text-sm text-slate-500">
            Hard-rollback weekly uploads. Cascades through search terms, recommendations,
            execution tasks and AI narratives. <span className="text-rose-600 font-semibold">This cannot be undone.</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5">
            <button data-testid="scope-current" onClick={() => setScope("current")} className={`px-3 py-1.5 text-xs font-semibold rounded-md ${scope === "current" ? "bg-slate-900 text-white" : "text-slate-600"}`}>Current brand</button>
            <button data-testid="scope-all" onClick={() => setScope("all")} className={`px-3 py-1.5 text-xs font-semibold rounded-md ${scope === "all" ? "bg-slate-900 text-white" : "text-slate-600"}`}>All brands</button>
          </div>
          <Button data-testid="dm-refresh-btn" onClick={load} variant="outline" className="rounded-lg gap-1.5">
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </Button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="uploads-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200">
            <tr>
              <th className="px-4 py-3 text-left">Brand</th>
              <th className="px-4 py-3 text-left">Week</th>
              <th className="px-4 py-3 text-left">Source files</th>
              <th className="px-4 py-3 text-right">Search Terms</th>
              <th className="px-4 py-3 text-right">Recs</th>
              <th className="px-4 py-3 text-right">Tasks</th>
              <th className="px-4 py-3 text-right">Memos</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={9} className="px-4 py-12 text-center text-slate-400">Loading…</td></tr>}
            {!loading && !uploads.length && (
              <tr><td colSpan={9} className="px-4 py-12 text-center text-slate-500 text-sm">No uploads found.</td></tr>
            )}
            {!loading && uploads.map((u) => {
              const recs = (u.counts?.campaign_actions || 0) + (u.counts?.listing || 0) + (u.counts?.gaps || 0);
              return (
                <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                  <td className="px-4 py-3 font-semibold text-slate-900">{u.brand_name || u.brand_id?.slice(0, 8)}</td>
                  <td className="px-4 py-3 text-slate-700">
                    <div>{u.week_start_date} → {u.week_end_date}</div>
                    <div className="text-[11px] text-slate-400">{(u.created_at || "").slice(0, 10)}</div>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600 max-w-xs truncate">
                    {(u.source_files || [u.file_name]).filter(Boolean).join(", ") || "—"}
                  </td>
                  <td className="px-4 py-3 text-right font-mono">{u.counts?.search_terms ?? 0}</td>
                  <td className="px-4 py-3 text-right font-mono">{recs}</td>
                  <td className="px-4 py-3 text-right font-mono">{u.counts?.tasks ?? 0}</td>
                  <td className="px-4 py-3 text-right font-mono">{u.counts?.narratives ?? 0}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${
                      u.upload_status === "Processed" ? "border-emerald-200 bg-emerald-50 text-emerald-700" :
                      u.upload_status === "Processing" ? "border-amber-200 bg-amber-50 text-amber-700" :
                      "border-slate-200 bg-slate-50 text-slate-600"}`}>{u.upload_status}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid={`dm-delete-${u.id}`}
                      onClick={() => { setConfirmTarget(u); setConfirmText(""); }}
                      className="text-rose-600 hover:text-rose-800 hover:bg-rose-50 gap-1.5"
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Rollback
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={!!confirmTarget} onOpenChange={(v) => { if (!v) { setConfirmTarget(null); setConfirmText(""); } }}>
        <DialogContent className="max-w-lg" data-testid="dm-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-rose-700 flex items-center gap-2">
              <ShieldAlert className="h-5 w-5" /> Hard-rollback this upload?
            </DialogTitle>
          </DialogHeader>
          {confirmTarget && (
            <div className="space-y-3 text-sm">
              <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-rose-900 text-xs">
                <div><span className="font-semibold">Brand:</span> {confirmTarget.brand_name}</div>
                <div><span className="font-semibold">Week:</span> {confirmTarget.week_start_date} → {confirmTarget.week_end_date}</div>
                <div><span className="font-semibold">Files:</span> {(confirmTarget.source_files || [confirmTarget.file_name]).filter(Boolean).join(", ")}</div>
                <div className="mt-1 grid grid-cols-2 gap-1">
                  <div>{confirmTarget.counts?.search_terms || 0} search terms</div>
                  <div>{confirmTarget.counts?.campaign_actions || 0} bidding recs</div>
                  <div>{confirmTarget.counts?.listing || 0} listing recs</div>
                  <div>{confirmTarget.counts?.gaps || 0} product gaps</div>
                  <div>{confirmTarget.counts?.tasks || 0} execution tasks</div>
                  <div>{confirmTarget.counts?.narratives || 0} AI memos</div>
                </div>
              </div>
              <p className="text-slate-700">
                To confirm, type the full upload id below:
              </p>
              <div className="font-mono text-[11px] bg-slate-100 rounded px-2 py-1 select-all break-all">{confirmTarget.id}</div>
              <Input
                data-testid="dm-confirm-input"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="Paste upload id to authorize"
                className="font-mono text-xs"
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setConfirmTarget(null); setConfirmText(""); }}>Cancel</Button>
            <Button
              data-testid="dm-confirm-delete"
              disabled={!confirmTarget || confirmText !== confirmTarget.id || deleting}
              onClick={onDelete}
              className="bg-rose-600 text-white hover:bg-rose-700"
            >
              {deleting ? "Deleting…" : "Delete forever"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
